import { ChangeEvent, JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import {
	AddEvent,
	Event,
	ChangeEvent as UpdateEvent
} from "utils/history/Event";
import { ListInfo } from "utils/list/List";
import { asTitle } from "utils/Utils";

import { Widget } from "./DashboardTypes";
import WidgetRow from "./WidgetRow";
import NewWidgetRow from "./NewWidgetRow";

export type WidgetTableProps = {
	elements: Array<Widget>;
	listInfo: ListInfo<string>;
	editable: boolean;

	onSelect: (keys: Array<string>, selected: boolean) => void;
	onChange: (event: Event) => void;
	onInsert: (event: Event) => void;
};

export default function WidgetTable(
	props: Readonly<WidgetTableProps>
): JSX.Element {
	const { t } = useTranslation();

	const handleInsert = useCallback(
		(widget: Widget): void => {
			props.onInsert(new AddEvent(`/${props.elements.length}`, widget));
		},
		[props.elements, props.onInsert]
	);

	const handleUpdate = useCallback(
		(uuid: string, value: string): void => {
			const index = props.elements.findIndex(w => w.uuid === uuid);
			const widget = props.elements[index];

			props.onChange(
				new UpdateEvent(`/${index}/name`, widget.name, value)
			);
		},
		[props.elements, props.onChange]
	);

	const handleChange = useCallback((uuid: string, event: UpdateEvent): void => {
		const index = props.elements.findIndex(w => w.uuid === uuid);
	
		event.applyContext(`/${index}`);
		props.onChange(event);
	}, [props.elements, props.onChange]);

	const handleSelectAll = useCallback(
		(_event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
			props.onSelect(props.listInfo.getVisibleKeys(), checked);
		},
		[props.onSelect, props.listInfo]
	);

	const handleSelect = useCallback(
		(uuid: string, checked: boolean): void => {
			props.onSelect([uuid], checked);
		},
		[props.onSelect]
	);

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell align="center" padding="checkbox">
						<Checkbox
							disabled={!props.editable}
							checked={props.listInfo.allVisibleSelected()}
							indeterminate={props.listInfo.someVisibleSelected()}
							onChange={handleSelectAll}
						/>
					</TableCell>
					<TableCell/>
					<TableCell>{asTitle(t("type"))}</TableCell>
					<TableCell>{asTitle(t("name"))}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{props.elements.map(widget => (
					<WidgetRow
						key={widget.uuid}
						widget={widget}
						editable={props.editable}
						selected={props.listInfo.isSelected(widget.uuid)}
						onSelect={handleSelect}
						onUpdate={handleUpdate}
						onChange={handleChange}
					/>
				))}
				{props.editable && (
					<NewWidgetRow
						key={props.elements.length}
						onCreate={handleInsert}
					/>
				)}
			</TableBody>
		</Table>
	);
}
